import {
  MAX_SUPPORTED_ARTIFACT_SCHEMA,
  AdminSummarySchema,
  AssetAccessSchema,
  ChallengeRankingSchema,
  CreatorRankingSchema,
  ReplaySchema,
  VariantRequestSchema,
  CreatorChallengeViewSchema,
  CreatorDraftSchema,
  CreatorVersionRequestSchema,
  LibraryEntrySchema,
  MapViewSchema,
  ManifestResponseSchema,
  OwnBestSchema,
  ProfilePatchSchema,
  ProfileSchema,
  ProductErrorSchema,
  PublicRoomViewSchema,
  ShareCreateSchema,
  ShareViewSchema,
  type CreatorChallengeView,
  type AdminSummary,
  type AssetAccess,
  type LibraryEntry,
  type MapView,
  type ManifestResponse,
  type Profile,
  type PublicRoomView,
  type ShareView,
} from "@urlcade/contracts";
import { endpoint } from "./config";

const BaseVersionIdSchema = CreatorDraftSchema.shape.mapVersionId;
const VariantHttpRequestSchema = VariantRequestSchema.extend({ baseVersionId: BaseVersionIdSchema.optional() }).strict();
const CreatorVersionHttpRequestSchema = CreatorVersionRequestSchema.extend({ baseVersionId: BaseVersionIdSchema.optional() }).strict();

type VariantHttpRequest = ReturnType<typeof VariantHttpRequestSchema.parse>;
type CreatorVersionHttpRequest = ReturnType<typeof CreatorVersionHttpRequestSchema.parse>;

export interface DiscoverPage { items: MapView[]; nextCursor: string | null; }
export class ProductApi {
  constructor(private readonly getAccessToken: () => Promise<string>) {}

  async getMap(mapId: string, signal?: AbortSignal): Promise<MapView> {
    return MapViewSchema.parse(await this.call(`/api/maps/${id(mapId)}`, { headers: await this.authHeaders(), signal: timed(signal) }));
  }

  async recapture(mapId: string): Promise<unknown> {
    return this.call(`/api/maps/${id(mapId)}/recapture`, {
      method: "POST",
      headers: await this.authHeaders("application/json"),
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  }

  async createVariant(mapId: string, input: { mode: import("@urlcade/contracts").ActiveMode; region?: import("@urlcade/contracts").Rect; patch?: import("@urlcade/contracts").CreatorPatch; baseVersionId?: string }): Promise<unknown> {
    const { baseVersionId, ...legacyInput } = input;
    const legacyPayload = VariantRequestSchema.parse({ ...legacyInput, idempotencyKey: crypto.randomUUID() });
    const payload: VariantHttpRequest = baseVersionId === undefined
      ? legacyPayload
      : VariantHttpRequestSchema.parse({ ...legacyPayload, baseVersionId });
    return this.call(`/api/maps/${id(mapId)}/variants`, { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify(payload) });
  }

  async getMapManifest(versionId: string, signal?: AbortSignal): Promise<ManifestResponse> {
    const query = new URLSearchParams({ maxArtifactSchema: String(MAX_SUPPORTED_ARTIFACT_SCHEMA) });
    return ManifestResponseSchema.parse(await this.call(`/api/map-versions/${id(versionId)}/manifest?${query}`, { headers: await this.authHeaders(), signal: timed(signal) }));
  }

  async getAssetAccess(versionId: string, signal?: AbortSignal): Promise<AssetAccess> {
    const response = await this.call(`/api/map-versions/${id(versionId)}/asset-access`, { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify({ maxArtifactSchema: MAX_SUPPORTED_ARTIFACT_SCHEMA }), signal: timed(signal) });
    return AssetAccessSchema.parse(response);
  }

  async bearerToken(): Promise<string> { return this.getAccessToken(); }

  async getLibrary(signal?: AbortSignal): Promise<LibraryEntry[]> {
    const body = await this.call("/api/library", { headers: await this.authHeaders(), signal: timed(signal) });
    if (!Array.isArray(body)) throw new Error("PRODUCT_RESPONSE_INVALID");
    return body.map((entry) => LibraryEntrySchema.parse(entry));
  }

  async saveLibrary(mapId: string, favorite?: boolean): Promise<LibraryEntry> {
    const body = await this.call("/api/library", { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify({ mapId: id(mapId), ...(typeof favorite === "boolean" ? { favorite } : {}) }) });
    return LibraryEntrySchema.parse(body);
  }

  async setFavorite(mapId: string, favorite: boolean): Promise<LibraryEntry> {
    const body = await this.call("/api/library", { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify({ mapId: id(mapId), favorite }) });
    return LibraryEntrySchema.parse(body);
  }

  async discover(input: { mode?: string; cursor?: string | null; signal?: AbortSignal } = {}): Promise<DiscoverPage> {
    const query = new URLSearchParams();
    if (input.mode) query.set("mode", input.mode);
    if (input.cursor) query.set("cursor", input.cursor);
    const body = record(await this.call(`/api/discover${query.size ? `?${query}` : ""}`, { headers: await this.authHeaders(), signal: timed(input.signal) }));
    if (!Array.isArray(body.items) || !(body.nextCursor === null || typeof body.nextCursor === "string")) throw new Error("PRODUCT_RESPONSE_INVALID");
    return { items: body.items.map((item) => MapViewSchema.parse(item)), nextCursor: body.nextCursor };
  }

  async publicRooms(signal?: AbortSignal): Promise<PublicRoomView[]> {
    const body = await this.call("/api/rooms/public", { headers: await this.authHeaders(), signal: timed(signal) });
    if (!Array.isArray(body)) throw new Error("PRODUCT_RESPONSE_INVALID");
    return body.map((item) => PublicRoomViewSchema.parse(item));
  }

  async getProfile(): Promise<Profile> {
    return ProfileSchema.parse(await this.call("/api/profile", { headers: await this.authHeaders() }));
  }

  async updateProfile(input: { nickname?: string; characterId?: string; locale?: "tr-TR" | "en-US" }): Promise<Profile> {
    const payload = ProfilePatchSchema.parse(input);
    return ProfileSchema.parse(await this.call("/api/profile", { method: "PATCH", headers: await this.authHeaders("application/json"), body: JSON.stringify(payload) }));
  }

  async deleteAccount(): Promise<void> {
    const body = record(await this.call("/api/profile", {
      method: "DELETE",
      headers: await this.authHeaders("application/json"),
      body: JSON.stringify({ confirm: "delete" }),
    }));
    if (body.deleted !== true) throw new Error("PRODUCT_RESPONSE_INVALID");
  }

  async createShare(mapId: string, expiresInHours = 168): Promise<ShareView> {
    const payload = ShareCreateSchema.parse({ expiresInHours });
    return ShareViewSchema.parse(await this.call(`/api/maps/${id(mapId)}/shares`, { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify(payload) }));
  }

  async revokeShare(shareId: string): Promise<void> {
    await this.call(`/api/shares/${id(shareId)}`, { method: "DELETE", headers: await this.authHeaders() });
  }

  async redeemShare(token: string): Promise<{ mapId: string; challengeId?: string }> {
    const body = record(await this.call("/api/shares/redeem", { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify({ token }) }));
    if (typeof body.mapId !== "string") throw new Error("PRODUCT_RESPONSE_INVALID");
    if (body.challengeId !== undefined && typeof body.challengeId !== "string") throw new Error("PRODUCT_RESPONSE_INVALID");
    return { mapId: id(body.mapId), ...(typeof body.challengeId === "string" ? { challengeId: id(body.challengeId) } : {}) };
  }

  async createCreatorChallenge(input: { mapVersionId: string; title: string; description?: string; visibility?: "private" | "unlisted" | "public" }): Promise<CreatorChallengeView> {
    const payload = CreatorDraftSchema.parse(input);
    return CreatorChallengeViewSchema.parse(await this.call("/api/challenges", { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify(payload) }));
  }

  async getCreatorChallenge(challengeId: string): Promise<CreatorChallengeView> {
    return CreatorChallengeViewSchema.parse(await this.call(`/api/challenges/${id(challengeId)}`, { headers: await this.authHeaders() }));
  }

  async getCreatorLeaderboard(challengeId: string, versionId: string, playerCount = 1): Promise<import("@urlcade/contracts").CreatorRanking[]> {
    const query = new URLSearchParams({ versionId: id(versionId), playerCount: String(Math.max(1, Math.min(4, Math.floor(playerCount)))) });
    const body = await this.call(`/api/challenges/${id(challengeId)}/leaderboard?${query}`, { headers: await this.authHeaders() });
    if (!Array.isArray(body)) throw new Error("PRODUCT_RESPONSE_INVALID");
    return CreatorRankingSchema.array().max(20).parse(body);
  }

  async getCreatorReplay(challengeId: string, replayId: string): Promise<import("@urlcade/contracts").Replay> {
    const challenge = id(challengeId);
    const replay = id(replayId);
    const parsed = ReplaySchema.parse(await this.call(`/api/challenges/${challenge}/replays/${replay}`, { headers: await this.authHeaders() }));
    if (parsed.id !== replay || parsed.challengeId !== challenge || !parsed.challengeVersionId || !parsed.mapVersionId || !parsed.rulesHash) throw new Error("VERSION_MISMATCH");
    return parsed;
  }

  async getMapVersionBest(versionId: string): Promise<import("@urlcade/contracts").OwnBest> {
    const version = id(versionId);
    const best = OwnBestSchema.parse(await this.call(`/api/map-versions/${version}/best`, { headers: await this.authHeaders() }));
    if (best.result && (best.result.mapVersionId !== version || !best.result.rulesHash || best.result.challengeVersionId !== null)) throw new Error("VERSION_MISMATCH");
    if (best.replay && (best.replay.mapVersionId !== version || best.replay.challengeId !== null || best.replay.challengeVersionId !== null || !best.replay.rulesHash)) throw new Error("VERSION_MISMATCH");
    if (best.result && best.replay && (best.result.artifactHash !== best.replay.artifactHash || best.result.rulesHash !== best.replay.rulesHash)) throw new Error("VERSION_MISMATCH");
    return best;
  }

  async createCreatorChallengeShare(challengeId: string, expiresInHours = 168): Promise<ShareView> {
    const payload = ShareCreateSchema.parse({ expiresInHours });
    return ShareViewSchema.parse(await this.call(`/api/challenges/${id(challengeId)}/shares`, { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify(payload) }));
  }

  async createChallengeVersion(challengeId: string, patch: import("@urlcade/contracts").CreatorPatch, baseVersionId?: string): Promise<unknown> {
    const legacyPayload = CreatorVersionRequestSchema.parse({ patch, idempotencyKey: crypto.randomUUID() });
    const payload: CreatorVersionHttpRequest = baseVersionId === undefined
      ? legacyPayload
      : CreatorVersionHttpRequestSchema.parse({ ...legacyPayload, baseVersionId });
    return this.call(`/api/challenges/${id(challengeId)}/versions`, { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify(payload) });
  }

  async publishMap(mapId: string): Promise<MapView> {
    return MapViewSchema.parse(await this.call(`/api/maps/${id(mapId)}/publish`, { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify({}) }));
  }

  async reportMap(input: { mapId: string; reason: "privacy" | "rights" | "unsafe" | "abuse" | "other"; details: string }): Promise<{ id: string; status: "open" }> {
    const body = record(await this.call("/api/reports", { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify(input) }));
    if (typeof body.id !== "string" || body.status !== "open") throw new Error("PRODUCT_RESPONSE_INVALID");
    return { id: body.id, status: "open" };
  }

  async getAdminSummary(): Promise<AdminSummary> {
    return AdminSummarySchema.parse(await this.call("/api/admin/summary", { headers: await this.authHeaders() }));
  }

  async adminReportAction(reportId: string, action: "dismiss" | "takedown", reason: string): Promise<void> {
    await this.call(`/api/admin/reports/${id(reportId)}/action`, { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify({ action, reason }) });
  }

  async adminPolicy(input: { host: string; action: "allow" | "block"; reason: string }): Promise<void> {
    await this.call("/api/admin/policies", { method: "POST", headers: await this.authHeaders("application/json"), body: JSON.stringify(input) });
  }

  private async authHeaders(contentType?: "application/json"): Promise<HeadersInit> {
    return { ...(contentType ? { "Content-Type": contentType } : {}), Authorization: `Bearer ${await this.getAccessToken()}`, Accept: "application/json" };
  }

  private async call(path: string, init: RequestInit): Promise<unknown> {
    let response: Response;
    try { response = await fetch(endpoint(path), { ...init, signal: init.signal ?? AbortSignal.timeout(12_000) }); }
    catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new Error("NETWORK_UNAVAILABLE");
    }
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const error = ProductErrorSchema.safeParse(body);
      if (error.success) throw new ProductApiError(error.data.code, error.data.messageKey, error.data.retryable);
      throw new Error(`HTTP_${response.status}`);
    }
    return body;
  }
}

export class ProductApiError extends Error {
  constructor(readonly code: string, readonly messageKey: string, readonly retryable: boolean) { super(code); this.name = "ProductApiError"; }
}

function id(value: string): string {
  if (!/^[0-9a-f-]{36}$/i.test(value)) throw new Error("INVALID_ID");
  return value;
}

function record(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error("PRODUCT_RESPONSE_INVALID");
  return value as Record<string, unknown>;
}

function timed(signal?: AbortSignal): AbortSignal {
  return signal ? AbortSignal.any([signal, AbortSignal.timeout(12_000)]) : AbortSignal.timeout(12_000);
}
